import { postStat } from 'api/stats/stats.api'
import { useState } from 'react'
import { systemFeatureFlagActive } from 'utils/systemUtils'

/** Enables the split view in the deviation and base settings views. */
export const SETTINGS_SPLIT_VIEW_FEATURE_FLAG = 'digital_twin_settings_split_view'
/** Enables the split view in the collected settings view. */
export const COLLECTED_SETTINGS_SPLIT_VIEW_FEATURE_FLAG = 'digital_twin_collected_settings_split_view'

/** The usage stat counting how many users choose the split view over the popup, and back. */
export const SETTING_EDITOR_MODE_STAT = 'digital-twin-setting-editor-mode'

const SETTING_EDITOR_MODE_STORAGE_KEY = 'digitalTwinSettingsEditorMode'
const COLLECTED_SETTINGS_EDITOR_MODE_STORAGE_KEY = 'digitalTwinCollectedSettingsEditorMode'

type SettingEditorMode = 'splitView' | 'modal'

function isSettingSplitViewAvailable(activeSystem: System | null, isCollectedSettings: boolean): boolean {
  return systemFeatureFlagActive(
    activeSystem,
    isCollectedSettings ? COLLECTED_SETTINGS_SPLIT_VIEW_FEATURE_FLAG : SETTINGS_SPLIT_VIEW_FEATURE_FLAG
  )
}

function readStoredEditorMode(storageKey: string): SettingEditorMode {
  try {
    return window.localStorage.getItem(storageKey) === 'splitView' ? 'splitView' : 'modal'
  } catch {
    return 'modal'
  }
}

function storeEditorMode(storageKey: string, mode: SettingEditorMode): void {
  try {
    window.localStorage.setItem(storageKey, mode)
  } catch {
    // Restricted browser storage: the choice then lasts until the page is reloaded.
  }
}

type SettingEditorModeParams = {
  activeSystem: System | null
  isCollectedSettings: boolean
}

type SettingEditorModeState = {
  splitViewAvailable: boolean
  opensInSplitView: boolean
  setOpensInSplitView: (opensInSplitView: boolean) => void
}

/**
 * Whether a digital twin setting is edited in a split view or in a modal. The view's system feature
 * flag decides first; only where it enables the split view does the user's own choice count, and
 * until a user chooses, settings open in the modal. The choice is remembered once for the deviation
 * and base settings views, and separately for collected settings. Each change of choice is counted as a
 * usage stat; picking the mode already chosen is not.
 */
export function useSettingEditorMode({
  activeSystem,
  isCollectedSettings,
}: SettingEditorModeParams): SettingEditorModeState {
  const splitViewAvailable = isSettingSplitViewAvailable(activeSystem, isCollectedSettings)
  const storageKey = isCollectedSettings ? COLLECTED_SETTINGS_EDITOR_MODE_STORAGE_KEY : SETTING_EDITOR_MODE_STORAGE_KEY
  const [chosenMode, setChosenMode] = useState<SettingEditorMode | null>(null)
  const mode = chosenMode ?? readStoredEditorMode(storageKey)

  const setOpensInSplitView = (opensInSplitView: boolean): void => {
    const nextMode = opensInSplitView ? 'splitView' : 'modal'
    if (nextMode === mode) {
      return
    }
    setChosenMode(nextMode)
    storeEditorMode(storageKey, nextMode)
    postStat(SETTING_EDITOR_MODE_STAT, opensInSplitView ? 'user-chose-split-view' : 'user-chose-popup')
  }

  return {
    splitViewAvailable,
    opensInSplitView: splitViewAvailable && mode === 'splitView',
    setOpensInSplitView,
  }
}
